import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, Download, Loader2, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

/**
 * Reusable bulk-upload dialog for CSV imports.
 *
 * Props:
 *   entityLabel        — e.g. "Resumes" or "Positions"
 *   templateUrl        — backend GET endpoint returning the CSV template (relative to API base)
 *   uploadUrl          — backend POST endpoint accepting `file` multipart (relative to API base)
 *   templateFileName   — file name suggested when the template downloads
 *   helpText           — short instructional copy shown inside the dialog
 *   columnSpec         — array of { name, required, description } describing required + optional columns
 *   onSuccess          — callback fired after a successful upload (parent should reload its data)
 *   triggerClassName   — optional override for the trigger button styling
 */
export default function BulkUploadDialog({
  entityLabel,
  templateUrl,
  uploadUrl,
  templateFileName,
  helpText,
  columnSpec = [],
  onSuccess,
  triggerClassName = 'border-blue-200 text-blue-700 hover:bg-blue-50',
}) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const fileInputRef = useRef(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}${templateUrl}`, { headers: headers(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = templateFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded — fill it in, then upload');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(csv)$/i.test(file.name)) {
      toast.error('Please upload a .csv file');
      return;
    }
    setUploading(true);
    setLastResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}${uploadUrl}`, formData, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
      });
      const created = res.data.created ?? res.data.imported ?? 0;
      const errors = res.data.errors || [];
      const skipped = res.data.skipped ?? 0;
      setLastResult({ created, errors, skipped });
      if (created > 0) {
        toast.success(`Imported ${created} ${entityLabel.toLowerCase()}${errors.length ? ` (${errors.length} row error${errors.length === 1 ? '' : 's'})` : ''}`);
        if (onSuccess) onSuccess(res.data);
      } else {
        toast.error(`No ${entityLabel.toLowerCase()} imported — see error list below`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to upload ${entityLabel}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setLastResult(null);
      }}
    >
      <DialogTrigger asChild>
        <Button data-testid={`bulk-upload-${entityLabel.toLowerCase()}-button`} variant="outline" className={triggerClassName}>
          <Upload size={16} className="mr-2" /> Bulk Upload
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`bulk-upload-${entityLabel.toLowerCase()}-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-blue-900">Bulk Upload {entityLabel}</DialogTitle>
          <DialogDescription className="text-slate-600">{helpText}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><FileSpreadsheet size={16} /> Step 1 — Download the template</h4>
            <p className="text-sm text-slate-600 mb-3">Includes the exact column names + 2 example rows. Edit it in Excel/Sheets and save as <strong>CSV</strong>.</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              disabled={downloading}
              data-testid={`bulk-upload-${entityLabel.toLowerCase()}-template-btn`}
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              {downloading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}
              Download {entityLabel} Template (.csv)
            </Button>
          </section>

          {columnSpec.length > 0 && (
            <section className="rounded-lg border border-slate-200 p-4">
              <h4 className="font-semibold text-slate-900 mb-2">Column reference</h4>
              <div className="text-xs text-slate-700 grid grid-cols-1 gap-1.5 max-h-56 overflow-y-auto">
                {columnSpec.map((c) => (
                  <div key={c.name} className="flex items-start gap-2">
                    <code className="bg-slate-100 px-1.5 py-0.5 rounded text-blue-800 font-mono shrink-0">{c.name}</code>
                    <span className="text-slate-600">
                      {c.required ? <span className="text-rose-600 font-semibold">required</span> : <span className="text-slate-400">optional</span>}
                      {' — '}{c.description}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2"><Upload size={16} /> Step 2 — Upload the filled CSV</h4>
            <p className="text-sm text-slate-700 mb-3">Duplicates are skipped automatically. Errors are reported row-by-row so you can fix and retry.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              disabled={uploading}
              data-testid={`bulk-upload-${entityLabel.toLowerCase()}-input`}
              className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 disabled:opacity-50"
            />
            {uploading && (
              <div className="mt-3 flex items-center gap-2 text-blue-700 text-sm">
                <Loader2 size={16} className="animate-spin" /> Uploading and processing…
              </div>
            )}
          </section>

          {lastResult && (
            <section className="rounded-lg border border-slate-200 p-4" data-testid={`bulk-upload-${entityLabel.toLowerCase()}-result`}>
              <h4 className="font-semibold text-slate-900 mb-2">Result</h4>
              <div className="text-sm text-slate-700 space-y-1">
                <div>✅ Created: <strong className="text-emerald-700">{lastResult.created}</strong></div>
                {lastResult.skipped > 0 && <div>⏭ Skipped (duplicates/invalid): <strong className="text-amber-700">{lastResult.skipped}</strong></div>}
                {lastResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-rose-700 font-semibold mb-1">⚠ {lastResult.errors.length} row error{lastResult.errors.length === 1 ? '' : 's'}:</div>
                    <ul className="max-h-40 overflow-y-auto text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded p-2 space-y-0.5">
                      {lastResult.errors.slice(0, 50).map((e, i) => (<li key={i}>• {e}</li>))}
                      {lastResult.errors.length > 50 && <li className="italic">…and {lastResult.errors.length - 50} more</li>}
                    </ul>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
